import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { authApi } from '../../api/auth';
import { ApiError, errorMessage } from '../../api/client';
import { E164, nameProblem, passwordProblem } from '../../auth/route-state';
import { Alert, Button, PasswordField, TextField } from '../../components/ui';

/**
 * Tenant self sign-up (POST /auth/register): name, email, password. The hub
 * generates the pool username; the email is the sign-in identity and unique
 * per account. Where the provider emails a confirmation code, the next step is
 * /sign-up/confirm, then the user signs in.
 */
export function SignUp() {
  const navigate = useNavigate();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  /** Set when the API answered 409: the address has an account, maybe one that never entered its code. */
  const [takenEmail, setTakenEmail] = useState<string | null>(null);

  const register = useMutation({
    mutationFn: async () => {
      const errors: Record<string, string> = {};
      const cleanEmail = email.trim();
      const cleanPhone = phone.replace(/[\s()-]/g, '');
      const firstProblem = nameProblem(firstName);
      if (firstProblem) errors.firstName = firstProblem;
      const lastProblem = nameProblem(lastName);
      if (lastProblem) errors.lastName = lastProblem;
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(cleanEmail))
        errors.email = 'Enter a valid email address.';
      if (cleanPhone && !E164.test(cleanPhone))
        errors.phone = 'Use international format, e.g. +14155552671.';
      const pw = passwordProblem(password, confirm);
      if (pw) errors.password = pw;
      setFieldErrors(errors);
      if (Object.keys(errors).length) return;

      setTakenEmail(null);
      let confirmation;
      try {
        ({ confirmation } = await authApi.register({
          email: cleanEmail,
          password,
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          ...(cleanPhone ? { phone: cleanPhone } : {}),
        }));
      } catch (err) {
        if (err instanceof ApiError && err.status === 409) setTakenEmail(cleanEmail);
        throw err;
      }
      if (confirmation.required) {
        navigate('/sign-up/confirm', {
          state: {
            confirm: {
              email: cleanEmail,
              destination: confirmation.destination,
              medium: confirmation.medium,
            },
          },
        });
        return;
      }
      navigate('/sign-in', {
        replace: true,
        state: { email: cleanEmail, notice: 'Your account is ready. Sign in to continue.' },
      });
    },
  });

  return (
    <>
      <h1 className="auth__title">Create your account</h1>
      <p className="auth__lead">Tenant owners sign up here. Staff accounts are created by staff.</p>
      {register.error ? <Alert kind="error">{errorMessage(register.error)}</Alert> : null}
      {takenEmail ? (
        <p className="auth__lead">
          Signed up with <strong>{takenEmail}</strong> before but never entered the code?{' '}
          <Link
            to="/sign-up/confirm"
            state={{ confirm: { email: takenEmail, destination: null, medium: null } }}
          >
            Enter it now
          </Link>
          .
        </p>
      ) : null}
      <form
        className="form"
        onSubmit={(e) => {
          e.preventDefault();
          register.mutate();
        }}
        noValidate
      >
        <div className="form__pair">
          <TextField
            label="First name"
            name="firstName"
            autoComplete="given-name"
            autoFocus
            required
            value={firstName}
            error={fieldErrors.firstName}
            onChange={(e) => setFirstName(e.target.value)}
          />
          <TextField
            label="Last name"
            name="lastName"
            autoComplete="family-name"
            required
            value={lastName}
            error={fieldErrors.lastName}
            onChange={(e) => setLastName(e.target.value)}
          />
        </div>
        <TextField
          label="Email"
          name="email"
          type="email"
          autoComplete="email"
          required
          value={email}
          error={fieldErrors.email}
          hint="You sign in with this address; the confirmation code goes here."
          onChange={(e) => setEmail(e.target.value)}
        />
        <TextField
          label="Phone number (optional)"
          name="phone"
          type="tel"
          inputMode="tel"
          autoComplete="tel"
          placeholder="+14155552671"
          value={phone}
          error={fieldErrors.phone}
          hint="Lets you sign in with a text-message code once verified."
          onChange={(e) => setPhone(e.target.value)}
        />
        <PasswordField
          label="Password"
          name="password"
          autoComplete="new-password"
          required
          value={password}
          hint="At least 8 characters."
          onChange={(e) => setPassword(e.target.value)}
        />
        <PasswordField
          label="Confirm password"
          name="confirmPassword"
          autoComplete="new-password"
          required
          value={confirm}
          error={fieldErrors.password}
          onChange={(e) => setConfirm(e.target.value)}
        />
        <Button
          type="submit"
          loading={register.isPending}
          disabled={!firstName || !lastName || !email || !password || !confirm}
        >
          Create account
        </Button>
      </form>
      <p className="auth__switch">
        Already have an account? <Link to="/sign-in">Sign in</Link>
      </p>
    </>
  );
}
